import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Paper, 
  Typography, 
  Link,
  Collapse,
  IconButton,
  useMediaQuery,
  Divider
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { 
  ExpandMore as ExpandMoreIcon,
  Cookie as CookieIcon,
  Security as SecurityIcon 
} from '@mui/icons-material';
import brandingConfig from '../../config/brandingConfig';

// Cookie-Einstellungen im localStorage speichern
const COOKIE_CONSENT_KEY = `${brandingConfig.appName.toLowerCase().replace(/\s/g, '_')}_cookie_consent`;

// Styled components
const BannerWrapper = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 1300,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  padding: theme.spacing(3),
  pointerEvents: 'none',
  [theme.breakpoints.down('sm')]: {
    padding: 0,
  },
}));

const CookieContainer = styled(Paper)(({ theme }) => ({
  pointerEvents: 'auto',
  width: '1200px',
  maxWidth: '95%',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[8],
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    width: '100%',
    borderRadius: `${theme.shape.borderRadius * 2}px ${theme.shape.borderRadius * 2}px 0 0`,
  },
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

const ButtonsSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  backgroundColor: theme.palette.grey[50],
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const ExpandButton = styled(IconButton)(({ theme, expanded }) => ({
  padding: 4,
  marginLeft: 'auto',
  color: 'inherit',
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const CookieBanner = () => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Beim Laden der Komponente prüfen, ob bereits Cookie-Einstellungen gespeichert sind
  useEffect(() => {
    const cookieConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!cookieConsent) {
      // Kurze Verzögerung für bessere UX
      const timer = setTimeout(() => {
        setOpen(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Cookie-Einstellungen speichern
  const handleAccept = (acceptAll = false) => {
    const consent = {
      necessary: true,
      functional: acceptAll,
      analytics: acceptAll,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setOpen(false);
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  if (!open) return null;

  return (
    <BannerWrapper>
      <CookieContainer>
        <HeaderSection>
          <CookieIcon />
          <Typography variant="h6" component="div">
            Datenschutzeinstellungen
          </Typography>
          <ExpandButton
            onClick={handleExpandClick}
            expanded={expanded}
            aria-expanded={expanded}
            aria-label="mehr anzeigen"
            size="small"
          >
            <ExpandMoreIcon />
          </ExpandButton>
        </HeaderSection>

        <ContentSection>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <SecurityIcon color="primary" sx={{ mt: 0.5 }} />
              <Typography variant="body1">
                Diese Anwendung verwendet Cookies für die Authentifizierung und Sessionverwaltung.
              </Typography>
            </Box>
            
            <Collapse in={expanded} timeout="auto">
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="textSecondary" paragraph>
                Wir legen großen Wert auf den Schutz Ihrer Daten. Unsere Anwendung verwendet ausschließlich technisch notwendige Cookies, die für den sicheren Betrieb erforderlich sind.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Es werden keine Marketing- oder Analyse-Cookies von Drittanbietern verwendet. 
                Weitere Informationen finden Sie in unserer{' '}
                <Link component={RouterLink} to="/datenschutz" color="primary">
                  Datenschutzerklärung
                </Link>.
              </Typography>
            </Collapse>
          </Box>
        </ContentSection>

        <ButtonsSection>
          <Button 
            variant="outlined"
            size={isMobile ? "large" : "medium"}
            onClick={() => handleAccept(false)}
            fullWidth={isMobile}
          >
            Nur notwendige Cookies
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            size={isMobile ? "large" : "medium"}
            onClick={() => handleAccept(true)}
            fullWidth={isMobile}
          >
            Alle Cookies akzeptieren
          </Button>
        </ButtonsSection>
      </CookieContainer>
    </BannerWrapper>
  );
};

export default CookieBanner;